import {
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/16/solid";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { fetchStoppedCampaignBatches } from "../../functions/fetchStoppedCampaignBatches";
import LoadingText from "../LoadingText";
import { Divider } from "../tailwind/divider";
import { Link } from "../tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { Text } from "../tailwind/text";
import { Tooltip } from "../Tooltip";

const MAX_BATCHES_SHOWN = 3;

export function ViewStoppedCampaigns({ customerId, className, isMobile }) {
  const navigate = useNavigate();
  const [stoppedBatches, setStoppedBatches] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!customerId) {
      setStoppedBatches([]);
      return;
    }

    setFetching(true);
    fetchStoppedCampaignBatches({ customerId })
      .then(setStoppedBatches)
      .catch((error) => {
        console.error("Error fetching stopped campaign batches:", error);
        setStoppedBatches([]);
      })
      .finally(() => setFetching(false));
  }, [customerId]);

  const batchesShown = stoppedBatches.slice(0, MAX_BATCHES_SHOWN);

  return (
    batchesShown.length > 0 && (
      <div className={clsx(className, "border rounded-xl p-3 shadow bg-white")}>
        <Link onClick={() => navigate("/campaigns/sendings")}>
          <div className="flex items-center hover:underline hover:cursor-pointer">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-1 mb-2" />
            <Text className="text-lg font-bold mb-2">Stopped campaigns</Text>
            {!isMobile && (
                  <div className="ml-2 text-gray-500 mb-1">
                    <Tooltip
                      size="4"
                      content={<div>These campaigns have been manually stopped. You can resume or cancel them.</div>}
                    />
                  </div>
                )}
            <ChevronRightIcon className="h-6 w-6 mb-1" />
          </div>
        </Link>
        <Divider />

        <Table dense className="[&_table]:table-fixed [&_table]:w-full">
          <TableHead>
            <TableRow>
              <TableHeader
                className={clsx(
                  isMobile ? "w-[70%]" : "w-[60%]",
                  "whitespace-normal break-words",
                )}
              >
                Campaign
              </TableHeader>
              {!isMobile && (
                <TableHeader className="w-[25%] whitespace-normal break-words">
                  First stopped date
                </TableHeader>
              )}
              <TableHeader
                className={clsx(
                  isMobile ? "w-[30%]" : "w-[15%]",
                  "text-center whitespace-normal break-words",
                )}
              >
                Remaining contacts
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetching ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 2 : 3}>
                  <LoadingText className="my-3 h-[24px]" />
                </TableCell>
              </TableRow>
            ) : (
              batchesShown.map((batch) => (
                <TableRow
                  key={batch.sendingBatchId}
                  href="/campaigns/sendings"
                  className="hover:bg-muted transition"
                >
                  <TableCell className="font-bold">
                    <span className="line-clamp-2 whitespace-normal break-words">
                      {batch.campaignName}
                    </span>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <span className="whitespace-normal break-words">
                        {batch.originalSendingDate.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          year: "numeric",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                        })}
                      </span>
                    </TableCell>
                  )}
                  <TableCell className="text-center">
                    {batch.totalContacts}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    )
  );
}
